import { Box, Grid2 as Grid, Typography } from '@mui/material';
import type { ComponentProps } from 'react';
import { lazy, Suspense } from 'react';
import ErrorBoundary from '@/components/ErrorBoundary';
import { combineSx } from '@/utils/combineSx';
import { useValuationsStore } from '../../../store/valuationsStore';
import { cardVariants } from '../../../theme/cardStyles';
import { Badge } from '../../Badge';
import { UpdatedAt } from '../../UpdatedAt';

const styles = {
  topSection: {
    width: '100%',
  },
  headerStyles: {
    fontFamily: 'Roboto',
    fontSize: '2rem',
    textWrap: 'balance',
    whiteSpace: 'wrap',
    fontWeight: '400',
    '@media (max-width: 1280px)': {
      fontSize: '1.5rem',
    },
  },
};

const mapStyles = {
  container: combineSx(
    {
      height: '12rem',
      cursor: 'pointer',
      overflow: 'hidden',
    },
    cardVariants({ interactive: true, padding: 'none' }),
  ),
};

type HeaderProps = ComponentProps<typeof Grid>;

const HeaderDetails = ({ children, ...gridProps }: HeaderProps) => {
  const selectedAsset = useValuationsStore.use.selectedAssetDetails();
  const disabled = selectedAsset === null;

  return (
    <Grid
      container
      alignItems='flex-start'
      flexDirection='column'
      {...gridProps}
      data-id='header-layout-root'
      sx={combineSx({
        paddingLeft: 'calc(var(--gap-section) * 3)',
      })}
    >
      <Grid component='hgroup' data-id='header-layout-header' mb='var(--gap-section)' width='100%'>
        <UpdatedAt date={selectedAsset?.reportUpdatedDate} />
        <Typography sx={styles.headerStyles} variant='h4' data-id='asset-details-name'>
          {disabled || !selectedAsset ? '-' : selectedAsset.displayName}
        </Typography>
      </Grid>

      {selectedAsset?.mAndAOpportunity ? (
        <Badge variant='ma' disabled={disabled} data-id='asset-details-ma-badge'>
          M&A OPPORTUNITY
        </Badge>
      ) : null}
      {children}
    </Grid>
  );
};

const LazyMapBox = lazy(() => import('./Map').then(module => ({ default: module.MapBox })));
const LazyActions = lazy(() => import('./Actions').then(module => ({ default: module.Actions })));
const mapSxs = {
  order: { xs: '1', md: '2' },
  gridColumn: { xs: 'span 100', md: 'span 35', lg: 'span 50' },
};

type DetailsHeaderProps = ComponentProps<typeof Box>;
const AssetDetailsHeader = ({ ...props }: DetailsHeaderProps) => {
  return (
    <Box
      component='header'
      sx={styles.topSection}
      display='grid'
      gridTemplateColumns='repeat(100, 1fr)'
      data-id='asset-details-top-section'
      {...props}
    >
      <HeaderDetails
        order={{ xs: '2', md: '1' }}
        gridColumn={{ xs: 'span 100', md: 'span 65', lg: 'span 50' }}
      >
        <ErrorBoundary>
          <Suspense fallback={null}>
            <LazyActions />
          </Suspense>
        </ErrorBoundary>
      </HeaderDetails>
      <ErrorBoundary>
        <Suspense
          fallback={
            <Box
              sx={mapStyles.container}
              {...mapSxs}
              data-id='asset-details-map-loading'
              aria-hidden='true'
            />
          }
        >
          <LazyMapBox sx={mapStyles.container} {...mapSxs} />
        </Suspense>
      </ErrorBoundary>
    </Box>
  );
};

export { AssetDetailsHeader };
